import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { runFinancialAnalysis } from '../../../api/educentialApi';
import {
  Calculator,
  PoundSterling,
  Users,
  FileText,
  Download,
  ChevronRight,
  ChevronLeft,
  Check,
  AlertCircle,
  Banknote,
  Plane,
  Building2
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const STEPS = [
  { id: 1, title: 'Study Details', subtitle: 'Programme and duration', icon: FileText },
  { id: 2, title: 'Tuition Fees', subtitle: 'Course costs', icon: PoundSterling },
  { id: 3, title: 'Location & Family', subtitle: 'Region and dependents', icon: Building2 },
  { id: 4, title: 'Additional Costs', subtitle: 'Visa, health, and travel', icon: Plane },
  { id: 5, title: 'Currency & Confirmation', subtitle: 'Final details', icon: Banknote },
];

const STUDY_PATHWAYS = [
  { value: 'BACHELORS', label: "Bachelor's Degree (Undergraduate)" },
  { value: 'MASTERS', label: "Master's Degree (Postgraduate)" },
  { value: 'PHD', label: 'PhD/Research Degree' },
  { value: 'TOP_UP', label: 'HND Top-up to Bachelor' },
  { value: 'PROFESSIONAL', label: 'Professional Course' },
  { value: 'OTHER', label: 'Other Programme' },
];

const DURATIONS = [
  { value: '1 year', label: '1 Year' },
  { value: '2 years', label: '2 Years' },
  { value: '3 years', label: '3 Years' },
  { value: '4 years', label: '4 Years' },
  { value: '5+ years', label: '5+ Years' },
];

const REGIONS = [
  { value: 'LONDON', label: 'London', livingCost: 12667 },
  { value: 'OUTSIDE_LONDON', label: 'Outside London', livingCost: 9207 },
];

const CURRENCIES = [
  { value: 'NGN', label: 'Nigerian Naira (₦)', symbol: '₦' },
  { value: 'GBP', label: 'British Pound (£)', symbol: '£' },
  { value: 'USD', label: 'US Dollar ($)', symbol: '$' },
];

export default function FinancialAnalysis() {
  const summaryRef = useRef(null);

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    studyPathway: '',
    duration: '',
    annualTuition: '',
    tuitionDeposit: '',
    region: '',
    dependants: '0',
    preferredCurrency: 'NGN',
    exchangeRateSource: '',
    ihs: '1035', // Immigration Health Surcharge per year
    visaFee: '490',
    tbTest: '79800',
    ielts: '80000',
    flights: '500000',
    otherLocal: '',
    accept: false,
    email: '',
  });

  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return formData.studyPathway && formData.duration;
      case 2:
        return formData.annualTuition;
      case 3:
        return formData.region;
      case 4:
        return true; // Optional fields
      case 5:
        return formData.preferredCurrency && formData.accept;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!validateStep()) {
      setError('Please fill in all required fields');
      return;
    }

    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
      setError('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setError('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async () => {
    if (!formData.accept) {
      setError('Please confirm that you understand this is an estimate');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        studyPathway: formData.studyPathway,
        programDuration: formData.duration,
        annualTuitionFee: parseFloat(formData.annualTuition) || 0,
        tuitionDepositPaid: parseFloat(formData.tuitionDeposit) || 0,
        institutionRegion: formData.region,
        numberOfDependents: parseInt(formData.dependants) || 0,
        preferredCurrency: formData.preferredCurrency,
        exchangeRateSource: formData.exchangeRateSource || undefined,
        immigrationHealthSurcharge: parseFloat(formData.ihs) || 1035,
        visaApplicationFee: parseFloat(formData.visaFee) || 490,
        tbTestCost: parseFloat(formData.tbTest) || 79800,
        languageTestCost: formData.ielts ? parseFloat(formData.ielts) : undefined,
        flightTicketEstimate: formData.flights || undefined,
        otherLocalTravelCosts: formData.otherLocal || undefined,
        confirmEstimate: 'true',
        reportEmail: formData.email || undefined,
      };

      const response = await runFinancialAnalysis('UK', payload);
      setResult(response);
    } catch (err) {
      console.error('Financial analysis error:', err);
      setError(err.response?.data?.error || 'Failed to calculate costs. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const exportPDF = async () => {
    if (!summaryRef.current) return;

    try {
      const canvas = await html2canvas(summaryRef.current, {
        scale: 2,
        backgroundColor: '#000000',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`UK-Study-Cost-Analysis-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
      window.alert('Failed to export PDF. Please try again.');
    }
  };

  if (result) {
    return <ResultsScreen result={result} formData={formData} summaryRef={summaryRef} onExportPDF={exportPDF} onStartOver={() => { setResult(null); setCurrentStep(1); }} />;
  }

  const currentStepData = STEPS.find(s => s.id === currentStep);

  return (
    <div className="bg-gray-50 min-h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-3">
              <Calculator className="w-6 h-6 text-yellow-500" />
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                UK Study Cost Calculator
              </h1>
            </div>
            <p className="text-gray-600 text-lg">
              Get a detailed breakdown of your study costs in the UK
            </p>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-gray-600">Step {currentStep} of {STEPS.length}</p>
              <p className="text-xl font-semibold text-gray-900 mt-1">{currentStepData.title}</p>
              <p className="text-sm text-gray-600 mt-0.5">{currentStepData.subtitle}</p>
            </div>
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-yellow-500/20 border-2 border-yellow-500/30">
              <currentStepData.icon className="w-8 h-8 text-yellow-500" />
            </div>
          </div>

          <div className="relative">
            <div className="h-2 bg-gray-50 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full transition-all duration-500"
                style={{ width: `${(currentStep / STEPS.length) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-8 md:p-12">
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <FormStep step={currentStep} formData={formData} onChange={handleChange} />

          {/* Navigation */}
          <div className="flex items-center justify-between mt-12 pt-8 border-t border-gray-1000">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="flex items-center space-x-2 px-6 py-3 bg-gray-50 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed text-gray-900 rounded-lg transition-all border-2 border-gray-200"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Previous</span>
            </button>

            <button
              onClick={handleNext}
              disabled={isSubmitting}
              className="flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold rounded-lg transition-all shadow-lg shadow-yellow-500/20"
            >
              <span>{currentStep === STEPS.length ? (isSubmitting ? 'Calculating...' : 'Calculate Costs') : 'Next'}</span>
              {!isSubmitting && <ChevronRight className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormStep({ step, formData, onChange }) {
  switch (step) {
    case 1:
      return (
        <div className="space-y-8">
          <FormField
            label="What programme do you want to study?"
            required
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {STUDY_PATHWAYS.map(pathway => (
                <button
                  key={pathway.value}
                  type="button"
                  onClick={() => onChange('studyPathway', pathway.value)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                    formData.studyPathway === pathway.value
                      ? 'border-2 border-yellow-500 bg-yellow-50 shadow-md'
                      : 'border-gray-1000 bg-gray-50 hover:border-gray-200'
                  }`}
                >
                  <p className="text-gray-900 font-medium">{pathway.label}</p>
                </button>
              ))}
            </div>
          </FormField>

          <FormField label="Programme duration" required>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {DURATIONS.map(dur => (
                <button
                  key={dur.value}
                  type="button"
                  onClick={() => onChange('duration', dur.value)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    formData.duration === dur.value
                      ? 'border-2 border-yellow-500 bg-yellow-50 shadow-md'
                      : 'border-gray-1000 bg-gray-50 hover:border-gray-200'
                  }`}
                >
                  <p className="text-gray-900 font-medium">{dur.label}</p>
                </button>
              ))}
            </div>
          </FormField>
        </div>
      );

    case 2:
      return (
        <div className="space-y-8">
          <FormField label="Annual tuition fee (£)" required>
            <div className="relative">
              <PoundSterling className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 w-5 h-5" />
              <input
                type="number"
                value={formData.annualTuition}
                onChange={(e) => onChange('annualTuition', e.target.value)}
                placeholder="e.g., 15000"
                className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100"
              />
            </div>
            <p className="mt-2 text-sm text-gray-600">Enter the annual fee for your chosen programme</p>
          </FormField>

          <FormField label="Tuition deposit paid (£)" optional>
            <div className="relative">
              <PoundSterling className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 w-5 h-5" />
              <input
                type="number"
                value={formData.tuitionDeposit}
                onChange={(e) => onChange('tuitionDeposit', e.target.value)}
                placeholder="e.g., 2000"
                className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100"
              />
            </div>
            <p className="mt-2 text-sm text-gray-600">If you've already paid a deposit, enter the amount here</p>
          </FormField>
        </div>
      );

    case 3:
      return (
        <div className="space-y-8">
          <FormField label="Where will you be studying?" required>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {REGIONS.map(region => (
                <button
                  key={region.value}
                  type="button"
                  onClick={() => onChange('region', region.value)}
                  className={`p-6 rounded-lg border-2 transition-all duration-200 text-left ${
                    formData.region === region.value
                      ? 'border-2 border-yellow-500 bg-yellow-50 shadow-md'
                      : 'border-gray-1000 bg-gray-50 hover:border-gray-200'
                  }`}
                >
                  <p className="text-gray-900 font-medium text-lg mb-1">{region.label}</p>
                  <p className="text-gray-600 text-sm">Living cost: £{region.livingCost.toLocaleString()}/year</p>
                </button>
              ))}
            </div>
          </FormField>

          <FormField label="Number of dependents (spouse/children)">
            <div className="relative">
              <Users className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 w-5 h-5" />
              <input
                type="number"
                min="0"
                max="10"
                value={formData.dependants}
                onChange={(e) => onChange('dependants', e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:bg-white focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100"
              />
            </div>
            <p className="mt-2 text-sm text-gray-600">Additional costs will be calculated for each dependent</p>
          </FormField>
        </div>
      );

    case 4:
      return (
        <div className="space-y-8">
          <FormField label="Immigration Health Surcharge (£/year)" optional>
            <input
              type="number"
              value={formData.ihs}
              onChange={(e) => onChange('ihs', e.target.value)}
              placeholder="1035"
              className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <p className="mt-2 text-sm text-gray-600">Default: £1,035 per year (2024 rate)</p>
          </FormField>

          <FormField label="Visa application fee (£)" optional>
            <input
              type="number"
              value={formData.visaFee}
              onChange={(e) => onChange('visaFee', e.target.value)}
              placeholder="490"
              className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <p className="mt-2 text-sm text-gray-600">Default: £490 (Student visa from outside UK)</p>
          </FormField>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField label="TB test cost" optional>
              <input
                type="number"
                value={formData.tbTest}
                onChange={(e) => onChange('tbTest', e.target.value)}
                placeholder="79800"
                className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
              <p className="mt-2 text-sm text-gray-600">In your local currency</p>
            </FormField>

            <FormField label="IELTS/English test cost" optional>
              <input
                type="number"
                value={formData.ielts}
                onChange={(e) => onChange('ielts', e.target.value)}
                placeholder="80000"
                className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
              <p className="mt-2 text-sm text-gray-600">In your local currency</p>
            </FormField>
          </div>

          <FormField label="Flight ticket estimate" optional>
            <input
              type="number"
              value={formData.flights}
              onChange={(e) => onChange('flights', e.target.value)}
              placeholder="500000"
              className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <p className="mt-2 text-sm text-gray-600">Round-trip ticket cost in your local currency</p>
          </FormField>
        </div>
      );

    case 5:
      return (
        <div className="space-y-8">
          <FormField label="Preferred currency for report" required>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {CURRENCIES.map(curr => (
                <button
                  key={curr.value}
                  type="button"
                  onClick={() => onChange('preferredCurrency', curr.value)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    formData.preferredCurrency === curr.value
                      ? 'border-2 border-yellow-500 bg-yellow-50 shadow-md'
                      : 'border-gray-1000 bg-gray-50 hover:border-gray-200'
                  }`}
                >
                  <p className="text-gray-900 font-medium">{curr.symbol} {curr.value}</p>
                </button>
              ))}
            </div>
          </FormField>

          <FormField label="Email for report (optional)">
            <input
              type="email"
              value={formData.email}
              onChange={(e) => onChange('email', e.target.value)}
              placeholder="your.email@example.com"
              className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-200 hover:border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <p className="mt-2 text-sm text-gray-600">We'll send you a detailed PDF report</p>
          </FormField>

          <div className="mt-8 p-6 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.accept}
                onChange={(e) => onChange('accept', e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-yellow-500 text-yellow-500 focus:ring-yellow-500"
              />
              <div className="flex-1">
                <p className="text-gray-900 font-medium mb-1">Confirmation Required</p>
                <p className="text-gray-300 text-sm leading-relaxed">
                  I understand this is an estimate based on current UKVI standards and exchange rates. Actual costs may vary.
                </p>
              </div>
            </label>
          </div>
        </div>
      );

    default:
      return null;
  }
}

function FormField({ label, required, optional, children }) {
  return (
    <div>
      <label className="block text-gray-900 font-medium mb-3 text-lg">
        {label}
        {required && <span className="text-yellow-500 ml-1">*</span>}
        {optional && <span className="text-gray-600 text-sm ml-2">(Optional)</span>}
      </label>
      {children}
    </div>
  );
}

function ResultsScreen({ result, formData, summaryRef, onExportPDF, onStartOver }) {
  const navigate = useNavigate();

  // Extract data from result
  const breakdown = result.breakdown || result.costBreakdown || {};
  const summary = result.summary || {};
  const currency = formData.preferredCurrency;
  const currencySymbol = CURRENCIES.find(c => c.value === currency)?.symbol || '£';

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return `${currencySymbol}${parseFloat(amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="bg-gray-50 min-h-full">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-500 mb-6">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Your Cost Analysis
          </h1>
          <p className="text-gray-600 text-lg">
            Here's a detailed breakdown of your estimated study costs
          </p>
        </div>

        {/* Results Content */}
        <div ref={summaryRef} className="space-y-8 mb-12">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
              <p className="text-yellow-400 text-sm font-medium mb-2">Total Cost</p>
              <p className="text-3xl font-bold text-gray-900">{formatCurrency(summary.totalCost || summary.grandTotal)}</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6">
              <p className="text-blue-400 text-sm font-medium mb-2">Tuition & Fees</p>
              <p className="text-3xl font-bold text-gray-900">{formatCurrency(breakdown.tuitionFees || breakdown.totalTuitionFees)}</p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-2xl p-6">
              <p className="text-purple-400 text-sm font-medium mb-2">Living Costs</p>
              <p className="text-3xl font-bold text-gray-900">{formatCurrency(breakdown.livingCosts || breakdown.totalLivingCosts)}</p>
            </div>
          </div>

          {/* Detailed Breakdown */}
          <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
              <FileText className="w-6 h-6 text-yellow-500" />
              <span>Cost Breakdown</span>
            </h2>

            <div className="space-y-4">
              {breakdown.tuitionFees && (
                <CostItem label="Tuition Fees" amount={breakdown.tuitionFees} currency={currencySymbol} />
              )}
              {breakdown.livingCosts && (
                <CostItem label="Living Costs" amount={breakdown.livingCosts} currency={currencySymbol} />
              )}
              {breakdown.immigrationHealthSurcharge && (
                <CostItem label="Immigration Health Surcharge" amount={breakdown.immigrationHealthSurcharge} currency={currencySymbol} />
              )}
              {breakdown.visaFee && (
                <CostItem label="Visa Application Fee" amount={breakdown.visaFee} currency={currencySymbol} />
              )}
              {breakdown.tbTest && (
                <CostItem label="TB Test" amount={breakdown.tbTest} currency={currencySymbol} />
              )}
              {breakdown.languageTest && (
                <CostItem label="English Language Test" amount={breakdown.languageTest} currency={currencySymbol} />
              )}
              {breakdown.flights && (
                <CostItem label="Flight Tickets" amount={breakdown.flights} currency={currencySymbol} />
              )}
            </div>

            {summary.proofOfFunds && (
              <div className="mt-8 pt-6 border-t border-yellow-500/30">
                <div className="flex items-center justify-between p-4 bg-yellow-500/10 rounded-lg">
                  <div>
                    <p className="text-yellow-400 font-medium mb-1">Proof of Funds Required (UKVI)</p>
                    <p className="text-gray-600 text-sm">Amount you need to show for visa application</p>
                  </div>
                  <p className="text-2xl font-bold text-yellow-500">{formatCurrency(summary.proofOfFunds)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Important Notes */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-400 font-medium mb-2">Important Notes</p>
                <ul className="text-gray-300 text-sm space-y-1 list-disc list-inside">
                  <li>These are estimates based on current UKVI standards</li>
                  <li>Actual costs may vary depending on your specific circumstances</li>
                  <li>Exchange rates fluctuate and may affect final costs</li>
                  <li>Additional costs may apply for specific courses or institutions</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
          <button
            onClick={onExportPDF}
            className="w-full sm:w-auto flex items-center justify-center space-x-2 px-8 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-semibold rounded-lg transition-all shadow-lg shadow-yellow-500/20"
          >
            <Download className="w-5 h-5" />
            <span>Download PDF</span>
          </button>

          <button
            onClick={() => navigate('/overseas/uk/readiness-assessment')}
            className="w-full sm:w-auto px-8 py-4 bg-gray-50 hover:bg-gray-500 text-gray-900 font-semibold rounded-lg transition-all border border-gray-200"
          >
            Check Your Readiness
          </button>

          <button
            onClick={onStartOver}
            className="w-full sm:w-auto px-8 py-4 bg-gray-50 hover:bg-gray-50 text-gray-900 rounded-lg transition-all border-2 border-gray-200"
          >
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}

function CostItem({ label, amount, currency }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-200 last:border-0">
      <p className="text-gray-300">{label}</p>
      <p className="text-gray-900 font-semibold">{currency}{parseFloat(amount).toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
    </div>
  );
}
